import { computed, onMounted } from 'vue'
import useGet from '../../../core/composables/api/useGet'
import { SurveyState } from '../../survey/models/survey-state'
import { useFeedbackHistory } from '../../view-feedback/composables/useFeedbackHistory'
import { useSurveys } from '../../survey/composables/useSurveys'
import { useOpenFeedbacks } from '../../open-feedbacks/composables/useOpenFeedbacks'
import { useFeedbackTasks } from '../../task/composables/useFeedbackTasks'
import type { Dashboard } from '../models/dashboard'
import { useCoachingInvitations } from './useCoachingInvitations'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { ApplicationModule } from '@/core/models/organization'
import { useApplicationModule } from '@/core/composables/use-application-module'
import { useFeedbackGoals } from '@/modules/task/composables/useFeedbackGoals'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '@/core/events/event-key'

export const useDashboard = () => {
  const { data, load, status, reload } = useGet<Dashboard>('dashboard')

  const { data: openFeedbacks, status: loadOpenFeedbacksStatus } = useOpenFeedbacks()
  const { feedbackTasks, recurringTasks, status: loadFeedbackTasksStatus } = useFeedbackTasks()
  const { data: surveys, status: loadSurveyStatus } = useSurveys({ states: [SurveyState.running] })
  const { data: feedbacks, status: loadFeedbacksStatus } = useFeedbackHistory({
    received: true,
    limit: 5,
  })
  const { coachingInvitations, coachingInvitationsStatus, updateCoachingInvitation } =
    useCoachingInvitations()

  const { feedbackGoals, status: loadFeedbackGoalsStatus } = useFeedbackGoals()

  onMounted(() => {
    load()
  })

  useEventBus(EventKey.objectiveUpdated).on(() => reload())

  const { hasModule } = useApplicationModule()

  const overallReady = computed(() => {
    return (
      loadOpenFeedbacksStatus.value == ApiRequestStatus.Success &&
      loadFeedbackTasksStatus.value == ApiRequestStatus.Success &&
      loadSurveyStatus.value == ApiRequestStatus.Success &&
      loadFeedbacksStatus.value == ApiRequestStatus.Success &&
      loadFeedbackGoalsStatus.value == ApiRequestStatus.Success &&
      (coachingInvitationsStatus.value == ApiRequestStatus.Success ||
        !hasModule(ApplicationModule.COACHING))
    )
  })

  const hasData = computed(() => {
    return (
      overallReady.value &&
      (openFeedbacks.value.length > 0 ||
        feedbackTasks.value.length > 0 ||
        surveys.value.length > 0 ||
        feedbacks.value.length > 0 ||
        (coachingInvitationsStatus.value == ApiRequestStatus.Success &&
          coachingInvitations.value.length > 0))
    )
  })

  const todos = computed(() => {
    return feedbackTasks.value.slice(0, 4)
  })

  const numberOfTodos = computed(() => {
    return feedbackTasks.value.length
  })

  return {
    data,
    status,
    surveys,
    loadSurveyStatus,
    feedbacks,
    loadFeedbacksStatus,
    openFeedbacks,
    loadOpenFeedbacksStatus,
    todos,
    numberOfTodos,
    recurringTasks,
    loadFeedbackTasksStatus,
    coachingInvitations,
    coachingInvitationsStatus,
    feedbackGoals,
    loadFeedbackGoalsStatus,
    updateCoachingInvitation,
    hasData,
  }
}
